import React, { createRef, ReactElement, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  InlineDiv,
  Container,
  ImageWrapper,
  CardNumber,
  CardExpirationDate,
  SpinnerContainer,
} from './SavedInstrumentRow.styles';
import isCardExpired from './isCardExpired/IsCardExpired';
import { Tooltip } from '../../../../tooltip/Tooltip';
import { SavedInstrumentStatus } from '../../../../../consts';
import { deleteSavedCard } from '../../../../../services/Api';
import DeleteCardButton from './deleteCard/DeleteCardButton';
import SpinnerDeleteCard from '../../../../spinner/SpinnerDeleteCard';

export interface ISaveInstrumentRowProps {
  savedInstrument: ISavedInstrument;
  handleClick: (id: string) => void;
  isSelected: boolean;
  isMobileOrTablet: boolean;
  paymentId: string;
  handleDeleteSavedCardButtonClick: (failedToDeleteCard: boolean, token: string) => void;
}

function SavedInstrumentRow(props: ISaveInstrumentRowProps): ReactElement {
  const { savedInstrument, isSelected, isMobileOrTablet, paymentId } = props;
  const { expirationDate } = savedInstrument;
  const isExpired =
    savedInstrument.status !== SavedInstrumentStatus.ACTIVE ||
    (savedInstrument.status === SavedInstrumentStatus.ACTIVE && isCardExpired(expirationDate));
  const labelTextId = isExpired ? 'savedInstrument.expiredDate.label' : 'savedInstrument.expiresDate.label';
  const containerRef = createRef<HTMLDivElement>();
  const [deleteCardInProgress, setDeleteCardInProgress] = useState(false);
  const [rowHeight, setRowHeight] = useState(0);

  const handleDeleteCard = async () => {
    const { handleDeleteSavedCardButtonClick } = props;
    try {
      if (containerRef.current) setRowHeight(containerRef.current.getBoundingClientRect().height);
      setDeleteCardInProgress(true);
      await deleteSavedCard(paymentId, savedInstrument.token);
      handleDeleteSavedCardButtonClick(false, savedInstrument.token);
    } catch (ex) {
      handleDeleteSavedCardButtonClick(true, savedInstrument.token);
    } finally {
      setDeleteCardInProgress(false);
    }
  };

  const handleRowClick = (event: React.MouseEvent) => {
    const {
      handleClick,
      savedInstrument: { token },
    } = props;
    if (isExpired) return;
    if (event.target instanceof Element && event.target.closest('.saved-instrument-row-item__delete-card-button')) {
      return;
    }
    handleClick(token);
  };

  const tooltipDataFor = `saved-instrument-row-item-${savedInstrument.token}`;
  const status = savedInstrument.status as string;

  if (deleteCardInProgress) {
    return (
      <SpinnerContainer height={rowHeight}>
        <SpinnerDeleteCard isLoading={deleteCardInProgress} />
      </SpinnerContainer>
    );
  }

  return (
    <Container
      data-testid={tooltipDataFor}
      data-iscapture="true"
      data-tip
      data-for={tooltipDataFor}
      onClick={handleRowClick}
      isSelected={isSelected}
      isExpired={isExpired}
      className={`saved-instrument-row-item-${status}__container`}
      ref={containerRef}
    >
      <ImageWrapper className="image__wrapper" isSelected={isSelected} isExpired={isExpired}>
        <img src={savedInstrument.image} alt={savedInstrument.brand} />
      </ImageWrapper>
      <InlineDiv className="saved-instrument-row-item__card-details">
        <CardNumber className="saved-instrument-row-item__card-number">
          <FormattedMessage id="savedInstrument.card.label" />: {savedInstrument.name}
        </CardNumber>
        <CardExpirationDate className="saved-instrument-row-item__expiration-date">
          <FormattedMessage id={labelTextId} />: {savedInstrument.expirationDate}
        </CardExpirationDate>
      </InlineDiv>
      <Tooltip
        id={tooltipDataFor}
        parentRef={createRef<HTMLDivElement>()}
        showTooltip={isExpired}
        isMobileOrTablet={isMobileOrTablet}
        messageId="tooltip.savedInstrument.disabled.expired_card"
        invokerRef={containerRef.current}
      />
      <div className="saved-instrument-row-item__delete-card-button">
        <DeleteCardButton handleClick={handleDeleteCard} />
      </div>
    </Container>
  );
}

export default SavedInstrumentRow;
